/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { isCookieFetched, mercureFetchCookie, mercureSubscribe } from "@/src/services/mercure";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Conversation } from "@/src/types/Entities/Messaging/Conversation";
import { fetchCollection, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { useEffect } from "react";

export default function useConversations(enabled?: boolean) {
  const cookieFetched = isCookieFetched();
  const { customer } = useCustomer();
  const queryClient = useQueryClient();

  const { data: conversations, isLoading } = useQuery<Array<Conversation>, Error>({
    queryKey: queryKeys.collection("/conversations", { customerId: customer?.id }),
    queryFn: () => fetchCollection("/conversations"),
    staleTime: Infinity,
    enabled,
  });

  useEffect(() => {
    if (customer) {
      if (cookieFetched) {
        const eventSource = mercureSubscribe("/messages/refresh/" + customer.id, () => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.collection("/conversations", { customerId: customer?.id }),
          });
        });
        return () => {
          eventSource.close();
        };
      } else {
        mercureFetchCookie();
      }
    }
  }, [cookieFetched, customer]);

  const newMessages = conversations?.flatMap((conversation) => conversation.containedMessages.filter((containedMessage) => !containedMessage.readByMe));

  return { conversations, isLoading, newMessages };
}
